import React from 'react';
import { Modal, Button, TextField, Autocomplete, FormControl, InputLabel, Select as MuiSelect, MenuItem, CircularProgress, IconButton } from '@mui/material';
import { Close, Add, ArrowForward, Edit, Check, Delete, Clear } from '@mui/icons-material';
import type { UnifiedModalItem, LocalYardSpot, ZoneTrailer, ExistingTrailer } from '../../pages/YardSpotterApp/types';
import { lineageCheckIn } from 'utils/lineage';
import LocationVerification from './LocationVerification';

interface UnifiedDetailsModalProps {
  open: boolean;
  selectedUnifiedItem: UnifiedModalItem | null;
  /** The facility being shown, for the Location Information's verified-location line. */
  facilityName?: string;
  selectedSpot: LocalYardSpot | null;
  trailerSearchInput: string;
  addTrailerScac: string;
  addTrailerNumber: string;
  addTrailerStatus: 'EMPTY' | 'LOADED';
  addTrailerShipmentNumber: string;
  addTrailerError: string | null;
  showCreateAnywayButton?: boolean;
  isAddingTrailer: boolean;
  isUnassigningTrailer: boolean;
  isEditingSpotTrailerNotes: boolean;
  spotTrailerNotesValue: string;
  savingSpotTrailerNotes: boolean;
  newShipmentReference: string;
  shipmentReferenceError: string | null;
  isUpdatingShipment: boolean;
  showRemoveShipmentConfirm: boolean;
  selectedExistingTrailer: ExistingTrailer | null;
  getAllExistingTrailers: () => ExistingTrailer[];
  isTrailerOOS: (trailer: LocalYardSpot | ZoneTrailer) => boolean;
  formatDirection: (direction: string) => string;
  isRemoveShipmentButtonEnabled: () => boolean;
  onClose: () => void;
  onMoveSpotToSpot: (spot: LocalYardSpot) => void;
  onMoveZoneTrailerToYardSpot: (trailer: ZoneTrailer) => void;
  onMoveToDock: (spot: LocalYardSpot) => void;
  onMoveZoneTrailerToDock: (trailer: ZoneTrailer) => void;
  onTrailerSearchInputChange: (value: string) => void;
  onAddTrailerScacChange: (value: string) => void;
  onAddTrailerNumberChange: (value: string) => void;
  onAddTrailerStatusChange: (value: 'EMPTY' | 'LOADED') => void;
  onAddTrailerShipmentNumberChange: (value: string) => void;
  onAddTrailerToSpot: () => void;
  onCreateAnyway?: () => void;
  onUnassignTrailer: () => void;
  onEditSpotTrailerNotes: (editing: boolean) => void;
  onSpotTrailerNotesValueChange: (value: string) => void;
  onSaveSpotTrailerNotes: () => void;
  onNewShipmentReferenceChange: (value: string) => void;
  onShipmentReferenceErrorChange: (error: string) => void;
  onUpdateTrailerShipment: () => void;
  onShowRemoveShipmentConfirm: (show: boolean) => void;
  onAutocompleteExistingTrailerSelect: (trailer: ExistingTrailer | null) => void;
  onClearExistingTrailer: () => void;
}

const UnifiedDetailsModal: React.FC<UnifiedDetailsModalProps> = ({
  open,
  selectedUnifiedItem,
  facilityName,
  selectedSpot,
  trailerSearchInput,
  addTrailerScac,
  addTrailerNumber,
  addTrailerStatus,
  addTrailerShipmentNumber,
  addTrailerError,
  showCreateAnywayButton,
  isAddingTrailer,
  isUnassigningTrailer,
  isEditingSpotTrailerNotes,
  spotTrailerNotesValue,
  savingSpotTrailerNotes,
  newShipmentReference,
  shipmentReferenceError,
  isUpdatingShipment,
  showRemoveShipmentConfirm,
  selectedExistingTrailer,
  getAllExistingTrailers,
  isTrailerOOS,
  formatDirection,
  isRemoveShipmentButtonEnabled,
  onClose,
  onMoveSpotToSpot,
  onMoveZoneTrailerToYardSpot,
  onMoveToDock,
  onMoveZoneTrailerToDock,
  onTrailerSearchInputChange,
  onAddTrailerScacChange,
  onAddTrailerNumberChange,
  onAddTrailerStatusChange,
  onAddTrailerShipmentNumberChange,
  onAddTrailerToSpot,
  onCreateAnyway,
  onUnassignTrailer,
  onEditSpotTrailerNotes,
  onSpotTrailerNotesValueChange,
  onSaveSpotTrailerNotes,
  onNewShipmentReferenceChange,
  onShipmentReferenceErrorChange,
  onUpdateTrailerShipment,
  onShowRemoveShipmentConfirm,
  onAutocompleteExistingTrailerSelect,
  onClearExistingTrailer,
}) => {
  if (!selectedUnifiedItem) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="flex justify-center items-center"
    >
      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={onClose}
          />
          <h1 className="text-xl font-bold mb-5">{selectedUnifiedItem.displayName} Details</h1>
          
          {/* Action buttons for trailers */}
          {(selectedUnifiedItem.trailerNumber || selectedUnifiedItem.type === 'zone_trailer') && (
            <div className="flex gap-2 mb-4">
              <button 
                className="bg-green-500 hover:bg-green-600 text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1"
                onClick={() => {
                  onClose();
                  if (selectedUnifiedItem.type === 'yard_spot') {
                    onMoveSpotToSpot(selectedUnifiedItem.originalData as LocalYardSpot);
                  } else {
                    onMoveZoneTrailerToYardSpot(selectedUnifiedItem.originalData as ZoneTrailer);
                  }
                }}
              >
                <ArrowForward fontSize="small" className="mr-2" />
                <span>{selectedUnifiedItem.type === 'yard_spot' ? 'Move to Spot' : 'Move to Yard Spot'}</span>
              </button>
              <button 
                className={`${
                  (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                  (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                } text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1`}
                disabled={
                  (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                  (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                }
                onClick={() => {
                  onClose();
                  if (selectedUnifiedItem.type === 'yard_spot') {
                    onMoveToDock(selectedUnifiedItem.originalData as LocalYardSpot);
                  } else {
                    onMoveZoneTrailerToDock(selectedUnifiedItem.originalData as ZoneTrailer);
                  }
                }}
                title={
                  (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                  (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                    ? 'Trailer is out of service'
                    : 'Move to Dock'
                }
              >
                <ArrowForward fontSize="small" className="mr-2" />
                <span>Move to Dock</span>
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Driver Information */}
            {(selectedUnifiedItem.driverName || selectedUnifiedItem.driverPhone) && (
              <div className="p-3 border rounded bg-blue-50">
                <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Driver Information
                </h3>
                {selectedUnifiedItem.driverName && (
                  <p className="text-sm text-gray-600 mb-1">
                    Name: {selectedUnifiedItem.driverName}
                  </p>
                )}
                {selectedUnifiedItem.driverPhone && (
                  <p className="text-sm text-gray-600">
                    Phone: {selectedUnifiedItem.driverPhone}
                  </p>
                )}
              </div>
            )}

            {/* Trailer Information */}
            <div className="p-3 border rounded bg-green-50">
              <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 14.846 4.632 16.5 6.414 16.5H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 6H6.28l-.31-1.243A1 1 0 005 4H3z"/>
                </svg>
                Trailer Information
              </h3>
              
              {/* Show add trailer form if spot is empty and it's a yard spot */}
              {!selectedUnifiedItem.trailerNumber && selectedUnifiedItem.type === 'yard_spot' && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">No trailer assigned to this spot</p>
                  
                  {/* Show info header with clear button when existing trailer is selected */}
                  {selectedExistingTrailer ? (
                    <div className="mb-3 p-2 bg-blue-100 rounded border border-blue-300 flex items-center justify-between">
                      <div className="flex items-center">
                        <Check fontSize="small" className="text-blue-600 mr-1" style={{fontSize: '14px'}} />
                        <p className="text-xs text-blue-800 font-medium">
                          Existing trailer selected
                        </p>
                      </div>
                      <IconButton
                        size="small"
                        onClick={onClearExistingTrailer}
                        className="text-blue-600"
                        title="Clear selection and enter new trailer"
                      >
                        <Clear fontSize="small" style={{fontSize: '14px'}} />
                      </IconButton>
                    </div>
                  ) : (
                    <p className="text-xs text-blue-600 mb-3">
                      Search for an existing trailer or enter a new one
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    {/* Autocomplete to search existing trailers */}
                    <Autocomplete
                      key={selectedUnifiedItem?.displayName || 'autocomplete-key'}
                      options={getAllExistingTrailers()}
                      getOptionLabel={(option) => `${option.scac} ${option.trailerNumber}`}
                      getOptionDisabled={(option) => option.isAtDock === true}
                      inputValue={trailerSearchInput}
                      onInputChange={(event, newInputValue) => onTrailerSearchInputChange(newInputValue)}
                      filterOptions={(options, { inputValue }) => {
                        const searchValue = inputValue.toLowerCase().trim();
                        if (!searchValue) return options;
                        
                        return options.filter(option => {
                          const scac = (option.scac || '').toLowerCase();
                          const trailerNumber = (option.trailerNumber || '').toLowerCase();
                          
                          // Must have a trailer number to match
                          if (!trailerNumber) return false;
                          
                          // Match only on trailer number substring
                          return trailerNumber.includes(searchValue);
                        });
                      }}
                      blurOnSelect
                      clearOnBlur
                      renderOption={(props, option) => (
                        <li 
                          {...props} 
                          key={option.trailerUuid}
                          style={{
                            ...props.style,
                            opacity: option.isAtDock ? 0.5 : 1,
                            cursor: option.isAtDock ? 'not-allowed' : 'pointer',
                            backgroundColor: option.isAtDock ? '#f5f5f5' : props.style?.backgroundColor
                          }}
                        >
                          <div style={{ fontSize: '12px', width: '100%' }}>
                            <div style={{ 
                              fontWeight: 'bold',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>{option.scac} {option.trailerNumber}</span>
                              {option.isAtDock && (
                                <span style={{ 
                                  fontSize: '9px', 
                                  color: '#f44336',
                                  fontWeight: 'normal',
                                  marginLeft: '8px'
                                }}>
                                  AT DOCK
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              {option.location}
                              {option.isAtDock && ' (Cannot be moved from here)'}
                            </div>
                          </div>
                        </li>
                      )}
                      value={null}
                      onChange={(event, newValue) => {
                        if (newValue && !newValue.isAtDock) {
                          onAutocompleteExistingTrailerSelect(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Existing Trailers"
                          variant="outlined"
                          size="small"
                          placeholder="Start typing to search..."
                          inputProps={{ 
                            ...params.inputProps,
                            style: { fontSize: '12px' }
                          }}
                          InputLabelProps={{ style: { fontSize: '12px' } }}
                        />
                      )}
                      disabled={isAddingTrailer || !!selectedExistingTrailer}
                      sx={{
                        '& .MuiAutocomplete-input': { fontSize: '12px' },
                        '& .MuiInputBase-root': { fontSize: '12px' }
                      }}
                    />
                    
                    {/* Separate SCAC field */}
                    <TextField
                      label="SCAC"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={addTrailerScac}
                      onChange={(e) => onAddTrailerScacChange(e.target.value.toUpperCase())}
                      disabled={isAddingTrailer || !!selectedExistingTrailer}
                      placeholder="e.g., ABCD"
                      inputProps={{ 
                        style: { fontSize: '12px' },
                        readOnly: !!selectedExistingTrailer
                      }}
                      InputLabelProps={{ style: { fontSize: '12px' } }}
                    />
                    
                    {/* Separate Trailer Number field */}
                    <TextField
                      label="Trailer Number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={addTrailerNumber}
                      onChange={(e) => onAddTrailerNumberChange(e.target.value.toUpperCase())}
                      disabled={isAddingTrailer || !!selectedExistingTrailer}
                      placeholder="e.g., 12345"
                      inputProps={{ 
                        style: { fontSize: '12px' },
                        readOnly: !!selectedExistingTrailer
                      }}
                      InputLabelProps={{ style: { fontSize: '12px' } }}
                    />
                    
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel style={{ fontSize: '12px' }}>Status</InputLabel>
                      <MuiSelect
                        value={addTrailerStatus}
                        onChange={(e) => onAddTrailerStatusChange(e.target.value as 'EMPTY' | 'LOADED')}
                        label="Status"
                        disabled={isAddingTrailer || !!selectedExistingTrailer}
                        style={{ fontSize: '12px' }}
                        inputProps={{ 
                          readOnly: !!selectedExistingTrailer
                        }}
                      >
                        <MenuItem value="EMPTY" style={{ fontSize: '12px' }}>Empty</MenuItem>
                        <MenuItem value="LOADED" style={{ fontSize: '12px' }}>Loaded</MenuItem>
                      </MuiSelect>
                    </FormControl>
                    
                    {addTrailerError && (
                      <div className="space-y-2">
                        <p className="text-xs text-red-600">{addTrailerError}</p>
                        {showCreateAnywayButton && onCreateAnyway && (
                          <button
                            className="w-full text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={onCreateAnyway}
                            disabled={isAddingTrailer}
                          >
                            Create Anyway
                          </button>
                        )}
                      </div>
                    )}
                    
                    <button
                      className={`w-full text-white text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium ${
                        isAddingTrailer 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-500 hover:bg-green-600'
                      }`}
                      onClick={onAddTrailerToSpot}
                      disabled={isAddingTrailer}
                    >
                      {isAddingTrailer ? (
                        <>
                          <CircularProgress size={12} className="mr-1" style={{color: 'white'}} />
                          Adding Trailer...
                        </>
                      ) : (
                        <>
                          <Add fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                          Add Trailer to Spot
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {selectedUnifiedItem.trailerNumber && (
                <p className="text-sm text-gray-600 mb-1">
                  Number: {selectedUnifiedItem.trailerNumber}
                </p>
              )}
              {selectedUnifiedItem.carrierName && (
                <p className="text-sm text-gray-600 mb-1">
                  SCAC: {selectedUnifiedItem.carrierName}
                </p>
              )}
              {selectedUnifiedItem.status && (
                <p className="text-sm text-gray-600 mb-2">
                  Status: {selectedUnifiedItem.status}
                </p>
              )}
              {lineageCheckIn(selectedUnifiedItem.active_lineage) && (
                <p className="text-sm text-gray-600 mb-2">
                  Check-in: {new Date(lineageCheckIn(selectedUnifiedItem.active_lineage)!).toLocaleString()}
                </p>
              )}
              
              {/* Unassign Trailer Button */}
              {selectedUnifiedItem.trailerNumber && (
                <button
                  className={`mt-2 w-full text-white text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium ${
                    isUnassigningTrailer 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                  onClick={onUnassignTrailer}
                  disabled={isUnassigningTrailer}
                >
                  {isUnassigningTrailer ? (
                    <>
                      <CircularProgress size={12} className="mr-1" style={{color: 'white'}} />
                      Unassigning...
                    </>
                  ) : (
                    <>
                      <ArrowForward fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                      Unassign Trailer
                    </>
                  )}
                </button>
              )}
              
              {/* Trailer Notes Section */}
              {selectedUnifiedItem.active_lineage && (
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-700">Notes</span>
                    {!isEditingSpotTrailerNotes && (
                      <button
                        className="text-sky-600 text-xs hover:underline flex items-center"
                        onClick={() => onEditSpotTrailerNotes(true)}
                      >
                        <Edit fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                        {selectedUnifiedItem.active_lineage.notes ? 'Edit' : 'Add'}
                      </button>
                    )}
                  </div>
                  
                  {isEditingSpotTrailerNotes ? (
                    <div>
                      <textarea
                        value={spotTrailerNotesValue}
                        onChange={(e) => onSpotTrailerNotesValueChange(e.target.value)}
                        placeholder="Enter notes about this trailer..."
                        className="w-full p-2 border rounded h-16 mb-2 text-xs"
                        disabled={savingSpotTrailerNotes}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          className="text-gray-600 hover:text-gray-800 text-xs"
                          onClick={() => {
                            onSpotTrailerNotesValueChange(selectedUnifiedItem.active_lineage?.notes || '');
                            onEditSpotTrailerNotes(false);
                          }}
                          disabled={savingSpotTrailerNotes}
                        >
                          Cancel
                        </button>
                        <button
                          className="text-green-600 hover:text-green-800 text-xs flex items-center"
                          onClick={onSaveSpotTrailerNotes}
                          disabled={savingSpotTrailerNotes}
                        >
                          {savingSpotTrailerNotes ? (
                            <>
                              <div className="animate-spin h-2 w-2 border border-current border-t-transparent rounded-full mr-1"></div>
                              Saving...
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-2 border rounded bg-white">
                      {selectedUnifiedItem.active_lineage.notes ? (
                        <p className="text-xs text-gray-600">{selectedUnifiedItem.active_lineage.notes}</p>
                      ) : (
                        <p className="text-xs text-gray-500 italic">No notes added</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Shipment Information */}
            <div className="p-3 border rounded bg-yellow-50">
              <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
                </svg>
                Shipment Information
              </h3>
              
              {/* Current shipment info */}
              {selectedUnifiedItem.shipmentNumber && (
                <p className="text-sm text-gray-600 mb-2">
                  Current Reference: {selectedUnifiedItem.shipmentNumber}
                </p>
              )}
              {selectedUnifiedItem.direction && (
                <p className="text-sm text-gray-600 mb-2">
                  Direction: {formatDirection(selectedUnifiedItem.direction)}
                </p>
              )}

              {/* Editable shipment reference section */}
              {(selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid) && (
                <div className="mt-3 pt-3 border-t border-yellow-200">
                  <p className="text-xs text-gray-600 mb-2">
                    Update Shipment Reference:
                  </p>
                  {shipmentReferenceError && (
                    <div className="mb-2 p-2 bg-red-100 rounded">
                      <p className="text-xs text-red-700">
                        {shipmentReferenceError}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <TextField
                      fullWidth
                      size="small"
                      value={newShipmentReference}
                      onChange={(e) => {
                        onNewShipmentReferenceChange(e.target.value);
                        if (shipmentReferenceError) {
                          onShipmentReferenceErrorChange('');
                        }
                      }}
                      placeholder="Enter shipment reference number"
                      variant="outlined"
                      error={!!shipmentReferenceError}
                      sx={{ 
                        backgroundColor: 'white',
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          padding: '8px'
                        }
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={onUpdateTrailerShipment}
                      disabled={!newShipmentReference.trim() || isUpdatingShipment}
                      startIcon={isUpdatingShipment ? <CircularProgress size={16} /> : <Check />}
                      sx={{ 
                        fontSize: '0.75rem',
                        minWidth: '80px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {isUpdatingShipment ? 'Updating...' : 'Update'}
                    </Button>
                  </div>
                  {selectedUnifiedItem.shipmentNumber && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => onShowRemoveShipmentConfirm(true)}
                      disabled={!isRemoveShipmentButtonEnabled()}
                      sx={{
                        fontSize: '0.75rem',
                        opacity: !isRemoveShipmentButtonEnabled() ? 0.5 : 1,
                        mt: 1
                      }}
                    >
                      Remove Shipment Relationship
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Location Information */}
            <div className="p-3 border rounded bg-purple-50">
              <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Location Information
              </h3>
              {selectedUnifiedItem.spotNumber && (
                <p className="text-sm text-gray-600 mb-1">
                  Yard Spot: {selectedUnifiedItem.spotNumber}
                </p>
              )}
              {selectedUnifiedItem.currentZone && (
                <p className="text-sm text-gray-600 mb-1">
                  Zone: {selectedUnifiedItem.currentZone}
                </p>
              )}
              {selectedUnifiedItem.spotStatus && (
                <p className="text-sm text-gray-600 mb-1">
                  Spot Status: {selectedUnifiedItem.spotStatus}
                </p>
              )}
              {selectedUnifiedItem.puid && (
                <p className="text-sm text-gray-600">
                  PUID: {selectedUnifiedItem.puid}
                </p>
              )}
              <LocationVerification
                subject={{
                  type: selectedUnifiedItem.type,
                  lineage: selectedUnifiedItem.active_lineage,
                  occupied: !!selectedUnifiedItem.trailerNumber,
                  lastRtlsEmptyAt: selectedUnifiedItem.lastRtlsEmptyAt,
                  lastManualCheckAt: selectedUnifiedItem.lastManualCheckAt,
                }}
                place={{
                  facilityName,
                  zoneName: selectedUnifiedItem.zoneName || selectedUnifiedItem.currentZone,
                  spotName: selectedUnifiedItem.spotNumber,
                }}
              />
            </div>
          </div>

          {/* Yard Spot Notes */}
          {selectedUnifiedItem.notes && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs font-medium text-amber-800 mb-1">Spot Notes</p>
              <p className="text-sm text-amber-700">{selectedUnifiedItem.notes}</p>
            </div>
          )}

          <div className="mt-5 pt-3 border-t flex justify-end">
            <button
              className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UnifiedDetailsModal;

